import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { homePath } from "@/components/nav";

const FLOW = ["Rekap Penjualan", "Estimasi +30%", "Invoice Kelipatan 5", "Cetak Bulk Nota"];

function BrandPanel({ appName }) {
  return (
    <div className="hidden lg:flex sheet-bg text-white flex-col justify-between p-12">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-sm bg-emerald-400 text-emerald-950 flex items-center justify-center"><Wifi className="h-5 w-5" /></div>
        <span className="font-display font-bold text-lg" data-testid="login-app-name">{appName}</span>
      </div>
      <div>
        <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">Satu platform,<br />banyak perusahaan WiFi.</h1>
        <p className="mt-4 text-emerald-100/80 max-w-md">Setiap perusahaan punya data, harga, dan nota sendiri — terpisah aman, tercatat dalam audit log.</p>
        <div className="mt-10 grid grid-cols-4 border border-white/20 font-mono text-[11px]">
          {FLOW.map((f, i) => (
            <div key={f} className="border-r border-white/20 last:border-r-0 p-3">
              <div className="text-emerald-300">{String.fromCharCode(65 + i)}{i + 1}</div>
              <div className="mt-1 text-white/90 leading-snug">{f}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[11px] font-mono text-emerald-200/70">AFLINK · JPN · HAMZAH · TELSAKOM · MEGANET · SPEEDPLAY · JISKOM · TLN · HASBINET</div>
    </div>
  );
}

export default function Login() {
  const { session, login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [appName, setAppName] = useState("Bulk Nota ISP");
  useEffect(() => {
    api.get("/admin/public-settings").then((r) => { setAppName(r.data.app_name); document.title = r.data.app_name; }).catch(() => {});
  }, []);

  if (session) return <Navigate to={homePath(session)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await login(username, password);
      nav(homePath(data));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <BrandPanel appName={appName} />
      <div className="flex items-center justify-center p-6 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8" data-testid="login-mobile-brand">
            <div className="h-10 w-10 rounded-sm sheet-bg text-white flex items-center justify-center font-display font-bold">BN</div>
            <span className="font-display font-bold text-lg">{appName}</span>
          </div>
          <div className="font-mono text-[11px] text-emerald-700 tracking-wider">MASUK KE AKUN</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Login Perusahaan</h2>
          <p className="text-sm text-slate-600 mt-2">Gunakan username dan password yang diberikan admin atau pemilik perusahaan.</p>
          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input id="username" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1 h-11" data-testid="login-username-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy || !username || !password} className="w-full h-11 bg-emerald-700 hover:bg-emerald-800" data-testid="login-form-submit-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Masuk
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
